// ============================================================================
// HAI Reporting Tool - Admin Data View Page
// Table view of all submitted data for administrators
// ============================================================================

import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { db } from '@/services/db';
import { getHCAICases } from '@/services/firestore.service';
import type { DeviceSnapshot, HAIRecord, SSISurveillance, Ward, HCAISurveillanceCase } from '@/types';

type DataType = 'devices' | 'hai' | 'ssi' | 'hcai';

export function AdminDataViewPage() {
  const [activeTab, setActiveTab] = useState<DataType>('devices');
  const [wards, setWards] = useState<Ward[]>([]);
  const [deviceData, setDeviceData] = useState<DeviceSnapshot[]>([]);
  const [haiData, setHAIData] = useState<HAIRecord[]>([]);
  const [ssiData, setSSIData] = useState<SSISurveillance[]>([]);
  const [hcaiData, setHCAIData] = useState<HCAISurveillanceCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWard, setSelectedWard] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [wardsData, devicesData, haiRecords, ssiRecords] = await Promise.all([
        db.wards.toArray(),
        db.deviceSnapshots.toArray(),
        db.haiRecords.toArray(),
        db.ssiSurveillance.toArray(),
      ]);

      // Load HCAI cases from local DB first
      let hcaiCases = await db.hcaiCases.toArray();

      // If no local HCAI cases, fetch from Firestore
      if (hcaiCases.length === 0) {
        console.log('[AdminDataView] No local HCAI cases, fetching from Firestore...');
        try {
          hcaiCases = await getHCAICases();
        } catch (firestoreError) {
          console.error('[AdminDataView] Error fetching from Firestore:', firestoreError);
        }
      }

      setWards(wardsData);
      setDeviceData(devicesData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      setHAIData(haiRecords.sort((a, b) => new Date(b.periodEnd).getTime() - new Date(a.periodEnd).getTime()));
      setSSIData(ssiRecords.sort((a, b) => new Date(b.periodEnd).getTime() - new Date(a.periodEnd).getTime()));
      setHCAIData(hcaiCases.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));

      console.log('[AdminDataView] Loaded HCAI cases:', hcaiCases.length);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWardName = (wardId: string) => {
    return wards.find(w => w.id === wardId)?.name || wardId;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredDeviceData = selectedWard === 'all'
    ? deviceData
    : deviceData.filter(d => d.wardId === selectedWard);

  const filteredHAIData = selectedWard === 'all'
    ? haiData
    : haiData.filter(d => d.wardId === selectedWard);

  const filteredSSIData = selectedWard === 'all'
    ? ssiData
    : ssiData.filter(d => d.wardId === selectedWard);

  const tabs = [
    { id: 'devices' as DataType, label: 'Device Snapshots', icon: '📊', count: filteredDeviceData.length },
    { id: 'hcai' as DataType, label: 'HCAI Surveillance', icon: '🔬', count: hcaiData.length },
    { id: 'hai' as DataType, label: 'HAI Records', icon: '🦠', count: filteredHAIData.length },
    { id: 'ssi' as DataType, label: 'SSI Records', icon: '🏥', count: filteredSSIData.length },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Data Submissions</h2>
          <p className="text-gray-600 mt-2">
            View all submitted surveillance data across all wards
          </p>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="wardFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Ward
              </label>
              <select
                id="wardFilter"
                value={selectedWard}
                onChange={(e) => setSelectedWard(e.target.value)}
                className="input"
              >
                <option value="all">All Wards</option>
                {wards.map((ward) => (
                  <option key={ward.id} value={ward.id}>
                    {ward.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={loadData}
              className="btn btn-secondary mt-auto"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span>{tab.icon}</span>
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Device Snapshots Table */}
        {activeTab === 'devices' && (
          <div className="card overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Snapshots</h3>
            {filteredDeviceData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No device snapshot data available</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ward</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Peripheral Line</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catheter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ventilator</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeviceData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDate(record.date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{getWardName(record.wardId)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.patientsWithDevice.cvp}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.patientsWithDevice.pl}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.patientsWithDevice.urinaryCatheter}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.patientsWithDevice.ventilator}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(record.createdAt).toLocaleDateString('en-ZA')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* HAI Cases Table */}
        {activeTab === 'hai' && (
          <div className="card overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">HAI Case Reports</h3>
            {filteredHAIData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No HAI case data available</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ward</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CAUTI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CLABSI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">BSI Other</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHAIData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(record.periodStart)} - {formatDate(record.periodEnd)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{getWardName(record.wardId)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.cases.uti}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.cases.cabsi}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.cases.bsiOther}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.cases.vap}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                        {record.notes || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(record.createdAt).toLocaleDateString('en-ZA')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* HCAI Surveillance Cases Table */}
        {activeTab === 'hcai' && (
          <div className="card overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">HCAI Surveillance Cases</h3>
            {hcaiData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No HCAI surveillance cases available</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ward</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Infections</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reported By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {hcaiData.map((record: any) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{record.patientName || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.hospital || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.ward || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.admissionDate ? formatDate(record.admissionDate) : 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.infections?.length || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{record.reportedBy || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(record.createdAt).toLocaleDateString('en-ZA')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* SSI Surveillance Table */}
        {activeTab === 'ssi' && (
          <div className="card overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SSI Surveillance Records</h3>
            {filteredSSIData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No SSI surveillance data available</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ward</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedures</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Infections</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate (%)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSSIData.map((record) => {
                    const rate = record.procedures > 0 ? ((record.infections / record.procedures) * 100).toFixed(2) : '0.00';
                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(record.periodStart)} - {formatDate(record.periodEnd)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{getWardName(record.wardId)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{record.procedures}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{record.infections}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{rate}%</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {record.notes || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(record.createdAt).toLocaleDateString('en-ZA')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
